import {
    ArrowRight,
    Box,
    StatusBadge as ComponentsStatusBadge,
    formatDateWithoutTime,
    IconButton,
    LinkText,
    TextCell,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@bcc-ui/ui-components";
import classNames from "classnames";
import { t } from "i18next";
import React from "react";
import { Column, Row } from "react-table";

import { TenantKebabMenu } from "./TenantKebabMenu";
import { TableState } from "./TenantsTable";
import CopyToClipboard from "components/copy-to-clipboard/CopyToClipboard";
import KebabMenu from "components/kebab-menu/KebabMenu";
import { SkeletonLoader } from "components/skeleton-loader/SkeletonLoader";
import StatusBadge, { Status } from "components/status-badge/StatusBadge";
import DateCell from "components/table/DateCell";
import { deriveColumnWidth } from "components/table/Table";
import tableStyle from "components/table/table.scss";
import TextWithTooltip from "components/table/TextWithTooltip";
import TenantMenuItems from "components/tenants/TenantMenuItems";
import style from "components/tenants/tenants.scss";
import { AUTH_TENANT_ACCESS, AUTH_TENANT_CREATE, AUTH_TENANT_EDIT, AUTH_TENANT_VIEW } from "domain/authority";
import {
    calculateTrialDaysLeft,
    CombinedTier,
    contractTypeToTranslationMap,
    countDaysTillDeletion,
    LicensingModel,
    showDaysTillDeletion,
    Tenant,
    TenantType,
} from "domain/tenants";
import { generateTenantTypeToTranslationMap, generateTierToTranslationMap } from "domain/tierRelatedMaps";
import { ProductToRateList } from "services/licenses/LicenseService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { userSessionService } from "services/user/UserSessionService";
import buttonStyle from "styles/buttons.scss";

import testIds from "testIds.json";

export const createColumns = (
    rowRefs: React.MutableRefObject<React.RefObject<HTMLTableRowElement>[]>,
    fetchTenantInfoAllowed: boolean,
    showTenantProfile: (tenant: Tenant) => void,
    createDropdownArrow: (isExpanded: boolean) => React.JSX.Element,
    showConnectorLines: (depth: number) => React.JSX.Element,
    highlightText: (text: string, highlight: string) => React.JSX.Element,
    createPlaceholderTableRows: (row: Row<Tenant>) => void,
    fetchSubTenants: (row: Row<Tenant>) => void,
    createSelectedTenantHierarchy: (rows: Row<Tenant>[], selectedRowId: string) => Row<Tenant>[],
    showDelivery: (
        licensingModel: LicensingModel,
        parentModel: LicensingModel,
        parentType: TenantType,
        parentTier: CombinedTier,
        tenantTier: CombinedTier
    ) => boolean,
    showCommonLicenseCount: (licensingModel: string, commonLicenseCount?: number) => string | number,
    tableContainerRef: React.RefObject<HTMLDivElement>,
    onTenantEdit: () => void,
    onTenantDelete: () => void,
    parentLevel: number,
    setNewComponent: (value: boolean) => void,
    tenantUuid: (value: string) => void,
    tenantName: (value: string) => void,
    rateVersions: ProductToRateList[] | undefined,
    state: TableState,
    searchQuery: string,
    isTenantsTableFeatureEnabled: boolean
) => {
    const columns: Array<Column<Tenant>> = [];
    columns.push({
        //TODO BCC-7002 Remove falsy statement after Tenants table rewrite is done
        Header: () =>
            isTenantsTableFeatureEnabled ? (
                t("Common.customerName")
            ) : (
                <TextWithTooltip text={t("Common.customerName")} key="1" />
            ),
        //TODO BCC-7002 Remove falsy statement after Tenants table rewrite is done
        className: isTenantsTableFeatureEnabled ? undefined : style.kebabMenuOpen,
        accessor: "name",
        fixedToLeft: true,
        //TODO BCC-7002 Check if this could be broken down. Also this causes slowed reaction time to column resizing -> investigate.
        Cell: ({ rows, row, value, isLoading }) => {
            if (isTenantsTableFeatureEnabled) {
                return isLoading ? (
                    <SkeletonLoader />
                ) : (
                    <TextCell>
                        <Box
                            verticalAlignment={row.original.foundUser ? "Top" : "Center"}
                            horizontalAlignment={"Left"}
                            gap={"Xs"}
                        >
                            <TenantKebabMenu
                                tenant={row.original}
                                onTenantEdit={onTenantEdit}
                                onTenantDelete={onTenantDelete}
                                licenseCount={
                                    row.original.availableLicenses == undefined ? 0 : row.original.availableLicenses
                                }
                                userCount={row.original.totalUsers == undefined ? 0 : row.original.totalUsers}
                                workflowCount={
                                    row.original.totalWorkflows == undefined ? 0 : row.original.totalWorkflows
                                }
                                reportCount={
                                    row.original.totalReportsCount == undefined ? 0 : row.original.totalReportsCount
                                }
                                parentLevel={parentLevel - 1}
                                rowsUntilSubTenant={row.depth >= 1 ? createSelectedTenantHierarchy(rows, row.id) : []}
                                setNewComponent={setNewComponent}
                                setTenantUuid={tenantUuid}
                                setTenantName={tenantName}
                                deliveryAllowed={showDelivery(
                                    row.original.licensingModel,
                                    row.original.parentLicensingModel,
                                    row.original.parentType,
                                    row.original.parentTier,
                                    row.original.tier
                                )}
                                rateVersions={rateVersions}
                                parentContractType={
                                    row.original.parentType === "INTERNAL"
                                        ? row.original.contractType
                                        : row.original.parentContractType
                                }
                                rowTenantLevel={parentLevel + row.depth}
                                tenants={state.tenants
                                    .filter((tenant) => tenant.parentUuid !== undefined)
                                    .map((tenant) => ({
                                        uuid: tenant.uuid,
                                        name: tenant.name,
                                        parentUuid: tenant.parentUuid!,
                                        tier: tenant.tier,
                                        licensingModel: tenant.licensingModel,
                                        type: tenant.type,
                                    }))}
                                tableContainerRef={tableContainerRef}
                            />
                            {row.original.subTenantsCount > 0 && fetchTenantInfoAllowed && (
                                <IconButton
                                    noHoverStyle
                                    onClick={() => {
                                        if (row.subRows.length === 0) {
                                            createPlaceholderTableRows(row);
                                            fetchSubTenants(row);
                                        }
                                        row.toggleRowExpanded();
                                    }}
                                    testId={testIds.common.primaryView.table.expandArrow}
                                >
                                    <Tooltip>
                                        <TooltipTrigger>
                                            <ArrowRight rotateDown={row.isExpanded} />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            {row.isExpanded
                                                ? t("CustomersTable.tenantExpandedDropdownLabel", {
                                                      shownCount: row.subRows.length,
                                                      totalCount: row.original.subTenantsCount,
                                                      tenantName: value,
                                                  })
                                                : t("CustomersTable.tenantCollapsedDropdownLabel", {
                                                      tenantName: value,
                                                      totalCount: row.original.subTenantsCount,
                                                  })}
                                        </TooltipContent>
                                    </Tooltip>
                                </IconButton>
                            )}
                            <Box columnLayout={!!row.original.foundUser} verticalAlignment={"Top"}>
                                <LinkText
                                    as={"button"}
                                    onClick={() => {
                                        showTenantProfile(row.original);
                                        usageStatisticsService.sendEvent({
                                            category: Category.TENANT,
                                            action: Action.VIEW_TENANT_DETAILS,
                                        });
                                    }}
                                >
                                    {value}
                                </LinkText>
                                {row.original.foundUser && (
                                    <Box paddingTop={"Xxs"} gap={"Xxxs"}>
                                        {row.original.foundUserName && (
                                            <>{highlightText(row.original.foundUserName, searchQuery)}•</>
                                        )}
                                        {highlightText(row.original.foundUser, searchQuery)}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </TextCell>
                );
            } else {
                const isSubRow = row.depth >= 1;
                const rowRef = React.useRef<HTMLTableRowElement>(null);
                const menuRef = React.useRef<HTMLDivElement>(null);
                const [showAbove, setShowAbove] = React.useState(false);
                rowRefs.current.push(rowRef);

                const handleKebabClick = () => {
                    if (menuRef.current) {
                        const rect = menuRef.current.getBoundingClientRect();
                        setTimeout(() => {
                            const backupMenuHeight = 340;
                            const menuHeight =
                                (document.querySelector(".src-components-tenants-tenants__kebabMenu") as HTMLElement)
                                    ?.offsetHeight || backupMenuHeight;
                            const tableBottom =
                                tableContainerRef.current?.getBoundingClientRect().bottom || window.innerHeight;
                            const tableTop = tableContainerRef.current?.getBoundingClientRect().top || 0;
                            const headerHeight =
                                document.querySelector('[role="columnheader"]')?.getBoundingClientRect().height || 0;
                            setShowAbove(
                                rect.bottom + menuHeight > tableBottom &&
                                    rect.top - menuHeight >= tableTop + headerHeight
                            );
                        }, 0);
                    }
                };
                return (
                    <div
                        ref={rowRef}
                        className={classNames(style.alignment, {
                            [tableStyle.subRowBorder]: isSubRow || (row.depth === 0 && row.isExpanded),
                            [tableStyle.subRowNameCell]: isSubRow,
                            [tableStyle.expandedRow]: row.isExpanded,
                            [style.disabledContainer]: row.original.deletionStatus,
                        })}
                        data-depth={row.depth}
                    >
                        {isSubRow && showConnectorLines(row.depth)}
                        {isLoading ? (
                            <div className={style.nameCellLoader}>
                                <SkeletonLoader width={15} />
                                <SkeletonLoader />
                            </div>
                        ) : (
                            <>
                                <div className={style.kebabIcon} ref={menuRef} onClick={handleKebabClick}>
                                    {userSessionService.hasFeatureLicense("FEATURE_TENANT_MANAGEMENT") &&
                                        userSessionService.userHasAnyAuthority([
                                            AUTH_TENANT_ACCESS,
                                            AUTH_TENANT_EDIT,
                                            AUTH_TENANT_VIEW,
                                            AUTH_TENANT_CREATE,
                                        ]) && (
                                            <KebabMenu>
                                                <TenantMenuItems
                                                    uuid={row.original.uuid}
                                                    customerName={value}
                                                    status={row.original.status}
                                                    onTenantEdit={onTenantEdit}
                                                    onTenantDelete={onTenantDelete}
                                                    tier={row.original.tier}
                                                    creationDate={row.original.createdTimestamp}
                                                    tenantRegion={row.original.region}
                                                    expirationDate={row.original.expirationDate}
                                                    countryCode={row.original.countryCode}
                                                    contactName={row.original.contactName}
                                                    contactEmail={row.original.contactEmail}
                                                    salesforceLeadId={row.original.salesforceLeadId}
                                                    notes={row.original.notes}
                                                    salesforceAccountId={row.original.salesforceAccountId}
                                                    parentExpirationDate={row.original.parentExpirationDate}
                                                    licenses={row.original.productsLicensed}
                                                    licenseCount={
                                                        row.original.availableLicenses == undefined
                                                            ? 0
                                                            : row.original.availableLicenses
                                                    }
                                                    userCount={
                                                        row.original.totalUsers == undefined
                                                            ? 0
                                                            : row.original.totalUsers
                                                    }
                                                    workflowCount={
                                                        row.original.totalWorkflows == undefined
                                                            ? 0
                                                            : row.original.totalWorkflows
                                                    }
                                                    reportCount={
                                                        row.original.totalReportsCount == undefined
                                                            ? 0
                                                            : row.original.totalReportsCount
                                                    }
                                                    parentLevel={parentLevel - 1}
                                                    showMenuAbove={showAbove}
                                                    tenantType={row.original.type}
                                                    rowsUntilSubTenant={
                                                        isSubRow ? createSelectedTenantHierarchy(rows, row.id) : []
                                                    }
                                                    setNewComponent={setNewComponent}
                                                    setTenantUuid={tenantUuid}
                                                    setTenantName={tenantName}
                                                    licensingModel={row.original.licensingModel}
                                                    rateUuid={row.original.rateUuid}
                                                    deliveryAllowed={showDelivery(
                                                        row.original.licensingModel,
                                                        row.original.parentLicensingModel,
                                                        row.original.parentType,
                                                        row.original.parentTier,
                                                        row.original.tier
                                                    )}
                                                    parentLicensingModel={row.original.parentLicensingModel}
                                                    rateVersions={rateVersions}
                                                    parentType={row.original.parentType}
                                                    parentTier={row.original.parentTier}
                                                    salesforceContractId={row.original.salesforceContractId}
                                                    salesforceContractState={row.original.salesforceContractState}
                                                    salesforceContactEmail={row.original.salesforceContactEmail}
                                                    salesforceContractEndDate={row.original.salesforceContractEndDate}
                                                    parentDrawBackPrevention={row.original.parentDrawbackPrevention}
                                                    drawbackPrevention={row.original.drawbackPrevention}
                                                    contractType={row.original.contractType}
                                                    parentContractType={
                                                        row.original.parentType === "INTERNAL"
                                                            ? row.original.contractType
                                                            : row.original.parentContractType
                                                    }
                                                    automaticDeletion={row.original.automaticDeletion ?? false}
                                                    diskQuotaMib={row.original.diskQuotaMib}
                                                    uploadLimitMib={row.original.uploadLimitMib}
                                                    downloadLimitMib={row.original.downloadLimitMib}
                                                    depth={row.original.depth ?? 1}
                                                    rowTenantLevel={parentLevel + row.depth}
                                                    trialDaysLeft={calculateTrialDaysLeft(row.original)}
                                                    tenants={state.tenants
                                                        .filter((tenant) => tenant.parentUuid !== undefined)
                                                        .map((tenant) => ({
                                                            uuid: tenant.uuid,
                                                            name: tenant.name,
                                                            parentUuid: tenant.parentUuid!,
                                                            tier: tenant.tier,
                                                            licensingModel: tenant.licensingModel,
                                                            type: tenant.type,
                                                        }))}
                                                />
                                            </KebabMenu>
                                        )}
                                </div>

                                {row.original.subTenantsCount > 0 && fetchTenantInfoAllowed && (
                                    <button
                                        onClick={() => {
                                            if (row.subRows.length === 0) {
                                                createPlaceholderTableRows(row);
                                                fetchSubTenants(row);
                                            }
                                            row.toggleRowExpanded();
                                        }}
                                        className={style.subTenantToggleButton}
                                        data-testid={testIds.common.primaryView.table.expandArrow}
                                        aria-expanded={row.isExpanded}
                                    >
                                        <TextWithTooltip
                                            text={
                                                row.isExpanded
                                                    ? t("CustomersTable.tenantExpandedDropdownLabel", {
                                                          shownCount: row.subRows.length,
                                                          totalCount: row.original.subTenantsCount,
                                                          tenantName: value,
                                                      })
                                                    : t("CustomersTable.tenantCollapsedDropdownLabel", {
                                                          tenantName: value,
                                                          totalCount: row.original.subTenantsCount,
                                                      })
                                            }
                                        >
                                            {createDropdownArrow(row.isExpanded)}
                                        </TextWithTooltip>
                                    </button>
                                )}

                                <div className={classNames(style.customerNameCell, style.nameWithDropdown)}>
                                    <button
                                        onClick={() => {
                                            showTenantProfile(row.original);
                                            usageStatisticsService.sendEvent({
                                                category: Category.TENANT,
                                                action: Action.VIEW_TENANT_DETAILS,
                                            });
                                        }}
                                        className={buttonStyle.textButton}
                                    >
                                        <TextWithTooltip text={value} />
                                    </button>

                                    {row.original.foundUser && (
                                        <div className={style.userInfo}>
                                            {row.original.foundUserName && (
                                                <>
                                                    {highlightText(row.original.foundUserName, searchQuery)}
                                                    <span className={style.userInfoSeparator}>•</span>
                                                </>
                                            )}
                                            {highlightText(row.original.foundUser, searchQuery)}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                );
            }
        },
        //TODO BCC-7002 Remove falsy statement after Tenants table rewrite is done
        width: isTenantsTableFeatureEnabled ? 240 : deriveColumnWidth(24, tableContainerRef),
    });

    columns.push({
        //TODO BCC-7002 Remove falsy statement after Tenants table rewrite is done
        Header: () =>
            isTenantsTableFeatureEnabled ? (
                t("CustomersTable.usecaseType")
            ) : (
                <TextWithTooltip text={t("CustomersTable.usecaseType")} key="11" />
            ),
        accessor: "contractType",
        Cell: ({ row, cell: { value }, isLoading }) => {
            if (isTenantsTableFeatureEnabled) {
                const trialDaysLeft = calculateTrialDaysLeft(row.original) ?? 0;
                return isLoading ? (
                    <SkeletonLoader />
                ) : value === "TRIAL" ? (
                    <ComponentsStatusBadge
                        value={
                            trialDaysLeft > 0
                                ? t("CustomerProfile.trialTenantsNavPanel.trialDaysLeft", {
                                      days: trialDaysLeft,
                                  })
                                : t("CustomerProfile.trialTenantsNavPanel.trialEndedText")
                        }
                        status={trialDaysLeft > 0 ? Status.WARNING : Status.NEUTRAL}
                    />
                ) : (
                    <TextCell>{t("Common.productionTenantType")}</TextCell>
                );
                //TODO BCC-7002 Remove the following else statement after Tenants table rewrite has been completed.
            } else {
                const tenant = row.original;
                const trialDaysLeft = calculateTrialDaysLeft(tenant);
                const contractType = value ?? "PRODUCTION";

                let trialStatusBadge = null;

                if (contractType === "TRIAL") {
                    if (trialDaysLeft !== null && trialDaysLeft > 0) {
                        trialStatusBadge = (
                            <StatusBadge
                                values={[
                                    {
                                        status: Status.WARNING,
                                        title: t("CustomerProfile.trialTenantsNavPanel.trialDaysLeft", {
                                            days: trialDaysLeft,
                                        }),
                                    },
                                ]}
                            />
                        );
                    } else {
                        trialStatusBadge = (
                            <StatusBadge
                                values={[
                                    {
                                        status: Status.NEUTRAL,
                                        title: t("CustomerProfile.trialTenantsNavPanel.trialEndedText"),
                                    },
                                ]}
                            />
                        );
                    }
                }
                return (
                    <div
                        className={classNames(style.alignment, {
                            [style.disabledContainer]: row.original.deletionStatus,
                        })}
                    >
                        <TextWithTooltip text={contractTypeToTranslationMap(t).get(value)}>
                            {trialStatusBadge}
                        </TextWithTooltip>
                    </div>
                );
            }
        },
        //TODO BCC-7002 Remove falsy statement after Tenants table rewrite is done
        width: isTenantsTableFeatureEnabled ? 160 : deriveColumnWidth(14, tableContainerRef),
    });

    columns.push({
        //TODO BCC-7002 Remove falsy statement after Tenants table rewrite is done
        Header: () =>
            isTenantsTableFeatureEnabled ? (
                t("CustomersTable.bundle")
            ) : (
                <TextWithTooltip text={t("CustomersTable.bundle")} key="9" />
            ),
        accessor: "tier",
        Cell: (cellInfo) => {
            if (isTenantsTableFeatureEnabled) {
                return cellInfo.isLoading ? (
                    <SkeletonLoader />
                ) : (
                    <TextCell>
                        {generateTenantTypeToTranslationMap(t).get(cellInfo.row.original.type) +
                            " - " +
                            generateTierToTranslationMap(t).get(cellInfo.value)}
                    </TextCell>
                );
            }
            //TODO BCC-7002 Remove the following else statement after Tenants table rewrite has been completed.
            else {
                return (
                    <div
                        className={classNames(style.alignment, {
                            [style.disabledContainer]: cellInfo.cell.row.original.deletionStatus,
                        })}
                    >
                        {cellInfo.isLoading ? (
                            <SkeletonLoader />
                        ) : (
                            <TextWithTooltip
                                text={
                                    generateTenantTypeToTranslationMap(t).get(cellInfo.row.original.type) +
                                    " - " +
                                    generateTierToTranslationMap(t).get(cellInfo.value)
                                }
                            />
                        )}
                    </div>
                );
            }
        },
        //TODO BCC-7002 Remove falsy statement after Tenants table rewrite is done
        width: isTenantsTableFeatureEnabled ? 220 : deriveColumnWidth(14, tableContainerRef),
    });
    columns.push({
        //TODO BCC-7002 Remove falsy statement after Tenants table rewrite is done
        Header: () =>
            isTenantsTableFeatureEnabled ? (
                t("CustomersTable.commonLicenseCount")
            ) : (
                <TextWithTooltip text={t("CustomersTable.commonLicenseCount")} key="10" />
            ),
        accessor: "commonLicenseCount",
        Cell: (cellInfo) => {
            if (isTenantsTableFeatureEnabled) {
                return cellInfo.isLoading ? (
                    <SkeletonLoader />
                ) : (
                    <TextCell>
                        {showCommonLicenseCount(cellInfo.cell.row.original.licensingModel, cellInfo.cell.value)}
                    </TextCell>
                );
            }
            //TODO BCC-7002 Remove the following else statement after Tenants table rewrite has been completed.
            else {
                return (
                    <div
                        className={classNames(style.alignment, {
                            [style.disabledContainer]: cellInfo.cell.row.original.deletionStatus,
                        })}
                    >
                        {cellInfo.isLoading ? (
                            <SkeletonLoader />
                        ) : (
                            <TextWithTooltip
                                text={showCommonLicenseCount(
                                    cellInfo.cell.row.original.licensingModel,
                                    cellInfo.cell.value
                                )}
                            />
                        )}
                    </div>
                );
            }
        },
        //TODO BCC-7002 Remove falsy statement after Tenants table rewrite is done
        width: isTenantsTableFeatureEnabled ? 140 : deriveColumnWidth(14, tableContainerRef),
    });

    columns.push({
        //TODO BCC-7002 Remove falsy statement after Tenants table rewrite is done
        Header: () =>
            isTenantsTableFeatureEnabled ? t("Common.status") : <TextWithTooltip text={t("Common.status")} key="2" />,
        accessor: "status",
        Cell: (cellInfo) => {
            if (isTenantsTableFeatureEnabled) {
                return cellInfo.isLoading ? (
                    <SkeletonLoader />
                ) : (
                    <ComponentsStatusBadge
                        value={
                            cellInfo.value
                                ? t("Common.active")
                                : !showDaysTillDeletion(
                                        cellInfo.row.original.contractType,
                                        cellInfo.row.original.automaticDeletion
                                    )
                                  ? t("Common.inactive")
                                  : t("CustomersTable.daysTillDeletion", {
                                        daysTillDeletion: countDaysTillDeletion(
                                            cellInfo.cell.row.original.expirationDate
                                        ),
                                    })
                        }
                        status={
                            cellInfo.value
                                ? Status.SUCCESS
                                : !showDaysTillDeletion(
                                        cellInfo.row.original.contractType,
                                        cellInfo.row.original.automaticDeletion
                                    )
                                  ? Status.NEUTRAL
                                  : Status.ERROR
                        }
                    />
                );
            }
            //TODO BCC-7002 Remove the following else statement after Tenants table rewrite has been completed.
            else {
                return (
                    <div
                        className={classNames(style.alignment, {
                            [style.disabledContainer]: cellInfo.cell.row.original.deletionStatus,
                        })}
                    >
                        {cellInfo.isLoading ? (
                            <SkeletonLoader />
                        ) : (
                            <StatusBadge
                                values={[
                                    {
                                        status: cellInfo.value
                                            ? Status.SUCCESS
                                            : !showDaysTillDeletion(
                                                    cellInfo.row.original.contractType,
                                                    cellInfo.row.original.automaticDeletion
                                                )
                                              ? Status.NEUTRAL
                                              : Status.ERROR,
                                        title: cellInfo.value
                                            ? t("Common.active")
                                            : !showDaysTillDeletion(
                                                    cellInfo.row.original.contractType,
                                                    cellInfo.row.original.automaticDeletion
                                                )
                                              ? t("Common.inactive")
                                              : t("CustomersTable.daysTillDeletion", {
                                                    daysTillDeletion: countDaysTillDeletion(
                                                        cellInfo.cell.row.original.expirationDate
                                                    ),
                                                }),
                                    },
                                ]}
                                tooltip={true}
                            />
                        )}
                    </div>
                );
            }
        },
        //TODO BCC-7002 Remove falsy statement after Tenants table rewrite is done
        width: isTenantsTableFeatureEnabled ? 100 : deriveColumnWidth(8, tableContainerRef),
    });
    columns.push({
        //TODO BCC-7002 Remove falsy statement after Tenants table rewrite is done
        Header: () =>
            isTenantsTableFeatureEnabled ? (
                t("CustomersTable.contactEmail")
            ) : (
                <TextWithTooltip text={t("CustomersTable.contactEmail")} key="5" />
            ),
        accessor: "contactEmail",
        Cell: (cellInfo) => {
            if (isTenantsTableFeatureEnabled) {
                return cellInfo.isLoading ? (
                    <SkeletonLoader />
                ) : (
                    <TextCell>
                        {cellInfo.value}
                        {cellInfo.value && <CopyToClipboard value={cellInfo.value} />}
                    </TextCell>
                );
            }
            //TODO BCC-7002 Remove the following else statement after Tenants table rewrite has been completed.
            else {
                return (
                    <div
                        className={classNames(style.alignment, {
                            [style.disabledContainer]: cellInfo.cell.row.original.deletionStatus,
                        })}
                    >
                        {cellInfo.isLoading ? <SkeletonLoader /> : <TextWithTooltip text={cellInfo.value} />}
                        {cellInfo.value && <CopyToClipboard value={cellInfo.value} />}
                    </div>
                );
            }
        },
        //TODO BCC-7002 Remove falsy statement after Tenants table rewrite is done
        width: isTenantsTableFeatureEnabled ? 310 : deriveColumnWidth(14, tableContainerRef),
    });

    columns.push({
        //TODO BCC-7002 Remove falsy statement after Tenants table rewrite is done
        Header: () =>
            isTenantsTableFeatureEnabled ? (
                t("CustomersTable.emsAccountId")
            ) : (
                <TextWithTooltip text={t("CustomersTable.emsAccountId")} key="6" />
            ),
        accessor: "emsId",
        Cell: (cellInfo) => {
            if (isTenantsTableFeatureEnabled) {
                return cellInfo.isLoading ? <SkeletonLoader /> : <TextCell>{cellInfo.value}</TextCell>;
            }
            //TODO BCC-7002 Remove the following else statement after Tenants table rewrite has been completed.
            else {
                return (
                    <div
                        className={classNames(style.alignment, {
                            [style.disabledContainer]: cellInfo.cell.row.original.deletionStatus,
                        })}
                    >
                        {cellInfo.isLoading ? <SkeletonLoader /> : <TextWithTooltip text={cellInfo.value} />}
                    </div>
                );
            }
        },
        //TODO BCC-7002 Remove falsy statement after Tenants table rewrite is done
        width: isTenantsTableFeatureEnabled ? 160 : deriveColumnWidth(16, tableContainerRef),
    });

    columns.push({
        //TODO BCC-7002 Remove falsy statement after Tenants table rewrite is done
        Header: () =>
            isTenantsTableFeatureEnabled ? (
                t("CustomersTable.accountId")
            ) : (
                <TextWithTooltip text={t("CustomersTable.accountId")} key="7" />
            ),
        accessor: "salesforceAccountId",
        Cell: (cellInfo) => {
            if (isTenantsTableFeatureEnabled) {
                return cellInfo.isLoading ? <SkeletonLoader /> : <TextCell>{cellInfo.value}</TextCell>;
            }

            //TODO BCC-7002 Remove the following else statement after Tenants table rewrite has been completed.
            else {
                return (
                    <div
                        className={classNames(style.alignment, {
                            [style.disabledContainer]: cellInfo.cell.row.original.deletionStatus,
                        })}
                    >
                        {cellInfo.isLoading ? <SkeletonLoader /> : <TextWithTooltip text={cellInfo.value} />}
                    </div>
                );
            }
        },
        //TODO BCC-7002 Remove falsy statement after Tenants table rewrite is done
        width: isTenantsTableFeatureEnabled ? 160 : deriveColumnWidth(16, tableContainerRef),
    });

    columns.push({
        //TODO BCC-7002 Remove falsy statement after Tenants table rewrite is done
        Header: () =>
            isTenantsTableFeatureEnabled ? (
                t("CustomersTable.expirationDate")
            ) : (
                <TextWithTooltip text={t("CustomersTable.expirationDate")} key="7" />
            ),
        accessor: "expirationDate",
        Cell: (cellInfo) => {
            if (isTenantsTableFeatureEnabled) {
                return cellInfo.isLoading ? (
                    <SkeletonLoader />
                ) : (
                    <TextCell>{cellInfo.value ? formatDateWithoutTime(cellInfo.value) : t("Common.never")}</TextCell>
                );
            }

            //TODO BCC-7002 Remove the following else statement after Tenants table rewrite has been completed.
            else {
                let expirationDate;

                if (!cellInfo.isLoading) {
                    cellInfo.value
                        ? (expirationDate = <DateCell tooltip={true} value={cellInfo.value} withoutTime={true} />)
                        : (expirationDate = <TextWithTooltip text={t("Common.never")} />);
                }
                return (
                    <div
                        className={classNames(style.alignment, {
                            [style.disabledContainer]: cellInfo.cell.row.original.deletionStatus,
                        })}
                    >
                        {cellInfo.isLoading ? <SkeletonLoader /> : expirationDate}
                    </div>
                );
            }
        },

        //TODO BCC-7002 Remove falsy statement after Tenants table rewrite is done
        width: isTenantsTableFeatureEnabled ? 140 : deriveColumnWidth(14, tableContainerRef),
    });

    columns.push({
        //TODO BCC-7002 Remove falsy statement after Tenants table rewrite is done
        Header: () =>
            isTenantsTableFeatureEnabled ? t("Common.notes") : <TextWithTooltip text={t("Common.notes")} key="8" />,
        accessor: "notes",
        Cell: (cellInfo) => {
            if (isTenantsTableFeatureEnabled) {
                return cellInfo.isLoading ? <SkeletonLoader /> : <TextCell>{cellInfo.value}</TextCell>;
            }

            //TODO BCC-7002 Remove the following else statement after Tenants table rewrite has been completed.
            else {
                return (
                    <div
                        className={classNames(style.alignment, {
                            [style.disabledContainer]: cellInfo.cell.row.original.deletionStatus,
                        })}
                    >
                        {cellInfo.isLoading ? <SkeletonLoader /> : <TextWithTooltip text={cellInfo.value} />}
                    </div>
                );
            }
        },
        width: isTenantsTableFeatureEnabled ? 140 : deriveColumnWidth(14, tableContainerRef),
    });

    return columns;
};
